// Print Kit — pure helpers for the per-tenant print collateral system.
//
// The templates themselves live in web/public/print/ (12 print-approved HTML
// masters + bind.js, the dependency-free binding engine). This module is the
// app side of that contract: it loads the manifest (the registry every UI list
// is driven from), builds the UTM-tagged QR destinations, converts the copy
// editors' plain text to the engine's innerHTML convention and back, audits a
// piece for surviving placeholder content, and shapes the print-order payload.
// Nothing here touches the templates' markup or styling.
import qrcode from "qrcode-generator";

export const PRINT_BASE = "/print/";

export interface ManifestField { key: string; label: string; html: boolean }
export interface ManifestSlot { key: string; label: string }
export interface ManifestTemplate {
  id: string; file: string; name: string; purpose: string; prefix: string;
  /** "concrete" on the concrete service's masters (plan §13 item 20); absent = a fence piece */
  vertical?: string;
  trim: string; bleed: string; px: [number, number]; pages: number; dieCut?: string;
  fields: ManifestField[]; photos: ManifestSlot[]; sections: ManifestSlot[];
  qr: { key: string; label: string; url: string } | null;
  logoKey: string;
  product: { product: string; sides: string; qty: number[] };
}
export interface PrintManifest {
  colors: { key: string; label: string; default: string }[];
  templates: ManifestTemplate[];
  brandFields: { key: string; label: string; placeholder: string }[];
}

/* the manifest is a plain script that publishes window.TEMPLATE_MANIFEST — load
   it once from the served template folder so design can revise manifest.js and
   the templates together without an app build. */
let manifestPromise: Promise<PrintManifest> | null = null;
export function loadPrintManifest(): Promise<PrintManifest> {
  if (manifestPromise) return manifestPromise;
  manifestPromise = new Promise((resolve, reject) => {
    const w: any = window;
    if (w.TEMPLATE_MANIFEST) return resolve(w.TEMPLATE_MANIFEST);
    const s = document.createElement("script");
    s.src = PRINT_BASE + "manifest.js";
    s.onload = () => (w.TEMPLATE_MANIFEST ? resolve(w.TEMPLATE_MANIFEST) : reject(new Error("manifest.js loaded but published nothing")));
    s.onerror = () => { manifestPromise = null; reject(new Error("Couldn't load " + PRINT_BASE + "manifest.js")); };
    document.head.appendChild(s);
  });
  return manifestPromise;
}

/* ---- the tenant's kit, persisted under settings.printKit ---------------- */
export interface PrintKit {
  /** public token for the FenceWorks-hosted QR landing routes (/print/s/<token>/…) */
  token?: string;
  /** where QR codes point: the tenant's own domain (they redirect the paths) or the hosted routes */
  qrMode?: "domain" | "hosted";
  /** the template config, shaped like template-config.js — sparse, only overrides.
      config.brand is the fence kit's brand; config.brandConcrete holds what the
      owner typed in the brand panel under the Concrete lens (on top of the
      concrete brand card, lib/verticals.ts brandOf) */
  config: any;
  /** per-piece QR choice: generated from the destination URL, or an uploaded dynamic code */
  qr?: Record<string, { mode: "auto" | "upload"; url?: string }>;
  /** the order draft: template id → quantity (0/absent = not in the order) */
  cart?: Record<string, number>;
  /** apparel lines in the order draft (lib/printorders.ts ApparelLine) */
  apparel?: { id: string; garmentId: string; colorId: string; placementId: string; sizes: Record<string, number>; logo?: string | null }[];
  /** the order a Reorder copied the draft from (sent with the order) */
  reorderOf?: string | null;
  /** ids of pieces whose availability lines were reviewed, keyed by the config hash they were reviewed at */
  availabilityReviewed?: Record<string, string>;
}

/* ---- deep get/set on the sparse config ---------------------------------- */
export function cfgGet(obj: any, path: string): any {
  return String(path).split(".").reduce((a, k) => (a == null ? a : a[k]), obj);
}
export function cfgSet(obj: any, path: string, value: any): any {
  const out = JSON.parse(JSON.stringify(obj || {}));
  const p = String(path).split(".");
  let o = out;
  for (let i = 0; i < p.length - 1; i++) o = o[p[i]] = (o[p[i]] && typeof o[p[i]] === "object") ? o[p[i]] : {};
  if (value === null || value === undefined || value === "") delete o[p[p.length - 1]];
  else o[p[p.length - 1]] = value;
  return out;
}

/* ---- plain-text ⇄ innerHTML (the end-user editing convention) -----------
   Tenants never see markup: newlines become <br>, and *starred words* become
   the template's <span data-accent> runs. Round-trips both ways. */
export function htmlToPlain(html: string): string {
  let s = String(html == null ? "" : html);
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<span[^>]*data-accent[^>]*>([\s\S]*?)<\/span>/gi, (_m, inner) => "*" + inner.replace(/<[^>]+>/g, "") + "*");
  s = s.replace(/<[^>]+>/g, "");
  s = s.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  return s.replace(/[ \t]+\n/g, "\n").trim();
}
export function plainToHtml(text: string): string {
  let s = String(text == null ? "" : text);
  s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  s = s.replace(/\*([^*\n]+)\*/g, '<span data-accent>$1</span>');
  return s.replace(/\r?\n/g, "<br>");
}
/** visible-character count of a bound value (what the canvas actually has to fit) */
export function textLen(html: string): number { return htmlToPlain(html).replace(/\s+/g, " ").length; }
/** copy budget: the shipped placeholder's length is the design's budget; a
    little slack is fine, much more clips (canvases are overflow:hidden). */
export function budgetState(len: number, budget: number | undefined): "ok" | "tight" | "over" {
  if (!budget) return "ok";
  if (len > budget * 1.25 + 4) return "over";
  if (len > budget) return "tight";
  return "ok";
}

/* ---- brand ---------------------------------------------------------------- */
export const BRAND_PLACEHOLDERS: Record<string, string> = {
  companyName: "Example Fence Co.", phone: "555-555-0100", website: "examplefence.com", email: "owner@examplefence.com",
  ownerName: "Sam Rivera", city: "Your City, ST", region: "Your Region", tagline: "Built to Last.",
};
/** seed the brand panel from what the tenant already told us (Company Settings, sign-in) */
export function brandSeed(store: any, auth?: any): Record<string, string> {
  const co = (store && store.company) || {};
  const s = (store && store.settings) || {};
  const city = [co.city || s.companyCity, co.state || s.companyState].filter(Boolean).join(", ");
  const out: Record<string, string> = {};
  const name = co.name || s.companyName; if (name) out.companyName = name;
  const phone = co.phone || s.companyPhone; if (phone) out.phone = phone;
  if (co.email) out.email = co.email;
  if (city) out.city = city;
  const owner = auth && auth.user && auth.user.name; if (owner) out.ownerName = owner;
  const site = co.website || s.companyWebsite; if (site) out.website = cleanDomain(site);
  return out;
}
/* ---- lenses: which pieces, which brand (plan §13 item 20) -----------------
   The manifest carries both services' masters; the Concrete lens shows the
   ones marked vertical "concrete" and prints them under the concrete brand
   (Rise & Shine), the fence lens the rest under the kit's own brand. */
export const lensOfKit = (v: any): "fence" | "concrete" => (v === "concrete" ? "concrete" : "fence");
export function templatesFor(manifest: PrintManifest | null | undefined, lens: any): ManifestTemplate[] {
  const cx = lensOfKit(lens) === "concrete";
  return (manifest ? manifest.templates : []).filter((t) => (t.vertical === "concrete") === cx);
}
export const isConcretePiece = (tpl: ManifestTemplate | null | undefined): boolean => !!(tpl && tpl.vertical === "concrete");
/** where the brand panel writes under a lens */
export const brandKeyFor = (lens: any): "brand" | "brandConcrete" => (lensOfKit(lens) === "concrete" ? "brandConcrete" : "brand");
/** the concrete brand's seed, from the service's brand card (lib/verticals.ts brandOf(store, "concrete")) */
export function concreteBrandSeed(b: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (!b || typeof b !== "object") return out;
  if (b.name) out.companyName = String(b.name);
  if (b.phone) out.phone = String(b.phone);
  if (b.email) out.email = String(b.email);
  const site = b.domain || b.website; if (site) out.website = cleanDomain(String(site));
  if (b.logo) out.logoSrc = String(b.logo);
  return out;
}
const nonEmpty = (o: any): Record<string, string> => { const r: Record<string, string> = {}; for (const k in (o || {})) { const v = o[k]; if (v != null && String(v).trim() !== "") r[k] = v; } return r; };
/** the brand a lens prints: the fence kit's own brand, or the concrete brand —
    the owner, city and region carry over (same people, same town), the
    service's name, phone, site and logo come from its brand card, and anything
    typed in the panel under that lens wins */
export function brandFor(kit: PrintKit, lens: any, concreteSeed?: Record<string, string>): Record<string, string> {
  const cfg = (kit && kit.config) || {};
  const fence = nonEmpty(cfg.brand);
  if (lensOfKit(lens) !== "concrete") return fence;
  const shared: Record<string, string> = {};
  for (const k of ["ownerName", "city", "region"]) if (fence[k]) shared[k] = fence[k];
  return { ...shared, ...nonEmpty(concreteSeed), ...nonEmpty(cfg.brandConcrete) };
}
export function cleanDomain(v: string): string {
  return String(v || "").trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "").toLowerCase();
}
export function phoneDigits(v: string): string {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 11 && d[0] === "1" ? d.slice(1) : d;
}

/* ---- QR destinations ------------------------------------------------------
   One code per piece, keyed by utm_content. The manifest's qr.url carries the
   path + the full UTM query for the piece on the placeholder domain; the
   tenant's kit swaps the host — their own domain (they redirect the paths, see
   qr-utm-map.md) or the FenceWorks-hosted landing routes. The query string is
   preserved verbatim either way. */
export function hostedBase(apiBase: string | null | undefined, token: string | undefined): string | null {
  if (!apiBase || !token) return null;
  return apiBase.replace(/\/$/, "") + "/print/s/" + encodeURIComponent(token);
}
export function qrDestination(tpl: ManifestTemplate, kit: PrintKit, apiBase: string | null | undefined, brand?: Record<string, string> | null): string | null {
  if (!tpl.qr) return null;
  let u: URL;
  try { u = new URL(tpl.qr.url); } catch (e) { return null; }
  const path = u.pathname.replace(/\/$/, "");
  const query = u.search || "";
  const b = brand || (kit.config && kit.config.brand) || {};
  if (isConcretePiece(tpl)) {
    // the concrete site's own /go/<slug> route: the brand's domain and a BARE
    // path — the route adds the UTM tags and drops the scan's query — and never
    // the hosted routes, which don't serve /go/*
    const domain = cleanDomain(b.website || "");
    return domain ? "https://" + domain + path : null;
  }
  if (kit.qrMode === "hosted") {
    const base = hostedBase(apiBase, kit.token);
    if (!base) return null;
    return base + (path || "/") + query;
  }
  const domain = cleanDomain(b.website || "");
  if (!domain) return null;
  return "https://" + domain + path + query;
}
/** utm_content of a piece — the reporting key every scan groups on (a concrete
    piece's bare /go path carries no tag: its id groups the scans) */
export function qrContent(tpl: ManifestTemplate): string | null {
  if (!tpl.qr) return null;
  try { return new URL(tpl.qr.url).searchParams.get("utm_content") || tpl.id; } catch (e) { return null; }
}

/* print-safe QR as an SVG data URL: ECC level H (survives dirt, staples, sun),
   square, dark-on-light, 4-module quiet zone, no logo knockout. Vector, so it
   stays crisp at 600 dpi and survives PDF export with no network. */
export function qrSvgDataUrl(url: string): string {
  const qr = qrcode(0, "H");
  qr.addData(String(url));
  qr.make();
  const n = qr.getModuleCount();
  let d = "";
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
  const size = n + 8;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

/* ---- the routes a tenant's site must serve (qr-utm-map.md) ---------------- */
export const TENANT_ROUTES = [
  { path: "/door", pieces: "Door hanger", what: "Instant-estimate page, tenant-branded end to end. Price range ungated, then email-only capture (the hanger promises no phone number)." },
  { path: "/quote", pieces: "Yard sign · Sell sheet · Line card", what: "Receives tagged traffic — the instant estimator." },
  { path: "/finance", pieces: "Financing flyer", what: "Monthly-payment explainer + pre-qualify path." },
  { path: "/refer", pieces: "Referral insert", what: "Referral landing: prefilled SMS so the referrer composes nothing; call + email fallbacks." },
  { path: "/care", pieces: "Care guide", what: "The care guide, mirrored online." },
  { path: "/driven-posts", pieces: "Why driven posts", what: "Driven-posts explainer." },
  { path: "/review", pieces: "Review sheet", what: "302 to your Google review link, scan logged first." },
  { path: "/", pieces: "Business card", what: "Home page — receives tagged traffic." },
];

/* the concrete site's short links (concrete-website lib/go-redirects.ts): each
   forwards to the photo-estimate page with its own tags, so a scan is attributable */
export const CONCRETE_ROUTES = [
  { path: "/go/door", pieces: "Door hanger", what: "Forwards to the photo-estimate page, tagged door-hanger · print · qr." },
  { path: "/go/sign", pieces: "Yard sign", what: "The photo-estimate page, tagged yard-sign." },
  { path: "/go/card", pieces: "Business card", what: "The photo-estimate page, tagged business-card." },
  { path: "/go/neighbor", pieces: "Neighbour letter", what: "The photo-estimate page, tagged neighbor-postcard · neighbor-bundle — the street campaign's own line in the reports." },
  { path: "/go/review", pieces: "Invoice · review ask", what: "The review page, tagged invoice." },
  { path: "/go/refer", pieces: "Referral card", what: "The photo-estimate page, tagged referral · word-of-mouth." },
];

/* ---- placeholder audit ------------------------------------------------------
   "Never ship a tenant's kit with any placeholder visible." The audit is what
   gates Add-to-order: blocking findings mean placeholder art/copy or an
   invented price would print; warnings need a human look. brandKeys is the
   list of brand.* keys the piece actually uses (from the engine's tpl-read). */
/** where a finding is fixed — the page jumps to and highlights this control */
export type FixTarget = { kind: "brand" | "logo" | "qr" | "photo" | "field" | "availability"; key?: string };
export interface Finding { text: string; fix?: FixTarget; fixLabel?: string }
export interface Audit { blocking: Finding[]; warnings: Finding[] }
const BRAND_LABEL: Record<string, string> = { companyName: "Company name", phone: "Phone", website: "Website", email: "Email", ownerName: "Owner name", city: "City, ST", region: "Region / service area", tagline: "Tagline" };
const PRICE_KEYS: Record<string, string[]> = {
  "line-card": ["lc.row1Price", "lc.row2Price", "lc.row3Price", "lc.row4Price"],
  "financing-flyer": ["fin.tier1Mo", "fin.tier2Mo", "fin.tier3Mo", "fin.row1Amt", "fin.row1Mo36", "fin.row1Mo60", "fin.row2Amt", "fin.row2Mo36", "fin.row2Mo60", "fin.row3Amt", "fin.row3Mo36", "fin.row3Mo60", "fin.row4Amt", "fin.row4Mo36", "fin.row4Mo60", "fin.row5Amt", "fin.row5Mo36", "fin.row5Mo60"],
};
const LEGAL_KEYS: Record<string, string[]> = { "financing-flyer": ["fin.disclaimer", "fin.legal"], "line-card": ["lc.disclaimer"] };
export function auditPiece(tpl: ManifestTemplate, kit: PrintKit, opts: { brandKeys?: string[]; companyLogo?: string; budgets?: Record<string, number>; overrides?: Record<string, string>; brand?: Record<string, string> } = {}): Audit {
  const cfg = kit.config || {};
  const brand = opts.brand || cfg.brand || {};   // the lens's brand (brandFor) — the fence kit's unless told otherwise
  const blocking: Finding[] = [], warnings: Finding[] = [];
  // brand literals the piece prints — every one left empty prints the placeholder
  const keys = (opts.brandKeys && opts.brandKeys.length ? opts.brandKeys : ["brand.companyName", "brand.phone", "brand.website"]);
  keys.map(k => k.replace(/^brand\./, "")).filter(k => !(brand[k] && String(brand[k]).trim())).forEach(k => {
    blocking.push({ text: (BRAND_LABEL[k] || k) + " isn't set — this piece would print “" + (BRAND_PLACEHOLDERS[k] || k) + "”.", fix: { kind: "brand", key: k }, fixLabel: "Set it" });
  });
  // logo: per-piece override → brand logo → Company Settings logo → placeholder art
  if (!(cfgGet(cfg, tpl.logoKey) || brand.logoSrc || opts.companyLogo)) blocking.push({ text: "No logo yet — the placeholder logo would print.", fix: { kind: "logo" }, fixLabel: "Upload logo" });
  // QR slot — and, on a concrete piece, somewhere for it to land (the site's /go/* route lives on the brand's domain)
  if (tpl.qr && isConcretePiece(tpl) && !cleanDomain(brand.website || "")) blocking.push({ text: "No website for this brand — the QR code has nowhere to land. Set the concrete site's domain (Settings → Services, or the brand panel).", fix: { kind: "brand", key: "website" }, fixLabel: "Set it" });
  if (tpl.qr && !cfgGet(cfg, tpl.qr.key)) blocking.push({ text: "No QR code in the " + tpl.qr.label.toLowerCase() + " slot — the non-scannable placeholder would print.", fix: { kind: "qr" }, fixLabel: "Generate QR" });
  // photos: the shipped imagery is sample art (licensed fence photography, the
  // concrete before/after drawings) — it may print, but a tenant's own jobs
  // sell better, so say so
  const missingPhotos = tpl.photos.filter(p => !cfgGet(cfg, p.key));
  if (missingPhotos.length) warnings.push({ text: (missingPhotos.length === tpl.photos.length ? "Sample photos in every slot" : missingPhotos.length + " slot" + (missingPhotos.length > 1 ? "s" : "") + " still on sample photos") + " (" + missingPhotos.map(p => p.label).join(", ") + ") — fine to print, but your own job photos convert better.", fix: { kind: "photo", key: missingPhotos[0].key }, fixLabel: "Upload photos" });
  // illustrative economics: nothing here invents prices
  const priceKeys = PRICE_KEYS[tpl.id] || [];
  const untouched = priceKeys.filter(k => !cfgGet(cfg, k));
  if (untouched.length) blocking.push({ text: untouched.length === priceKeys.length ? "The prices are still the sample numbers — enter your own before this prints (they're illustrative, never a quote)." : untouched.length + " price field" + (untouched.length > 1 ? "s" : "") + " still show the sample numbers.", fix: { kind: "field", key: untouched[0] }, fixLabel: "Enter prices" });
  const legal = (LEGAL_KEYS[tpl.id] || []).filter(k => !cfgGet(cfg, k));
  if (legal.length) warnings.push({ text: "Disclaimer / legal text is the sample wording — have it checked for your state and lender.", fix: { kind: "field", key: legal[0] }, fixLabel: "Edit" });
  // availability lines expire — every print run gets a look (either door hanger)
  if (hasAvailability(tpl)) warnings.push({ text: "Availability lines (front pill, back box) must be reviewed before every print run.", fix: { kind: "availability" }, fixLabel: "Review" });
  // copy that outgrows its slot clips (the canvas never reflows)
  if (opts.overrides && opts.budgets) for (const k in opts.overrides) {
    if (budgetState(textLen(opts.overrides[k]), opts.budgets[k]) === "over") blocking.push({ text: "“" + htmlToPlain(opts.overrides[k]).slice(0, 40) + "…” is far longer than its slot (" + textLen(opts.overrides[k]) + "/" + opts.budgets[k] + " characters) and would clip.", fix: { kind: "field", key: k }, fixLabel: "Shorten" });
  }
  return { blocking, warnings };
}

/** does this piece carry availability lines (the door hangers' pill and box)? */
export const hasAvailability = (tpl: ManifestTemplate | null | undefined): boolean => !!(tpl && tpl.fields.some((f) => /avail/i.test(f.key)));
export const availabilityKeys = (tpl: ManifestTemplate | null | undefined): string[] => (tpl ? tpl.fields.filter((f) => /avail/i.test(f.key)).map((f) => f.key) : []);

/* ---- order payload ---------------------------------------------------------
   What the print provider receives per piece: identity, product spec from the
   manifest, quantity, and the fully resolved config (brand + overrides) so the
   provider (or our own headless-Chrome export) renders the identical file.
   Pricing comes back from the provider — nothing here invents it. */
export function resolvedConfig(kit: PrintKit, companyLogo?: string, brand?: Record<string, string> | null): any {
  const cfg = JSON.parse(JSON.stringify(kit.config || {}));
  // a lens's brand (brandFor) replaces the kit's — the concrete masters never print the fence company's name
  cfg.brand = brand ? { ...brand } : (cfg.brand || {});
  delete cfg.brandConcrete;   // the panel's own store, not a template key
  if (!cfg.brand.logoSrc && companyLogo) cfg.brand.logoSrc = companyLogo;
  return cfg;
}
export function orderItem(tpl: ManifestTemplate, quantity: number, config: any) {
  return {
    templateId: tpl.id, name: tpl.name, file: tpl.file,
    product: tpl.product.product, sides: tpl.product.sides,
    trim: tpl.trim, fullBleed: tpl.bleed, pages: tpl.pages, dieCut: tpl.dieCut || null,
    px: tpl.px, quantity, qrContent: qrContent(tpl), config,
  };
}
export function buildOrder(manifest: PrintManifest, kit: PrintKit, opts: { workspace?: string; company?: any; email?: string; companyLogo?: string; concreteBrand?: Record<string, string> | null }) {
  const cfg = resolvedConfig(kit, opts.companyLogo);
  // the concrete masters render under the concrete brand; one order can carry both services' pieces
  const cfgCx = opts.concreteBrand ? resolvedConfig(kit, undefined, opts.concreteBrand) : cfg;
  const cart = kit.cart || {};
  const items = manifest.templates.filter(t => (cart[t.id] || 0) > 0).map(t => orderItem(t, cart[t.id], isConcretePiece(t) ? cfgCx : cfg));
  const co = opts.company || {};
  return {
    kind: "print.order", createdAt: new Date().toISOString(), workspace: opts.workspace || null,
    contact: { company: cfg.brand.companyName || co.name || null, email: opts.email || co.email || null, phone: cfg.brand.phone || co.phone || null, shipTo: co.address || null },
    notes: "Files are full-bleed at 100% scale with bleed included — no crop marks, no printer margins. QR codes: scan-test from the printed proof before the run.",
    items,
  };
}
/** the copy of the payload that is safe to keep in the events table / an email body (inline images replaced with size markers) */
export function stripDataUrls(v: any): any {
  if (typeof v === "string") return v.startsWith("data:") ? `<inline image, ${Math.round(v.length * 0.75 / 1024)} KB>` : v;
  if (Array.isArray(v)) return v.map(stripDataUrls);
  if (v && typeof v === "object") { const o: any = {}; for (const k in v) o[k] = stripDataUrls(v[k]); return o; }
  return v;
}

/* ---- order status (shared by the tenant page and the owner console) ------- */
export const ORDER_STATUS: Record<string, { label: string; chip: string; hint: string }> = {
  quote_requested: { label: "Awaiting price", chip: "amber", hint: "We're pricing this — you'll get a pay link by email." },
  pending_payment: { label: "Ready to pay", chip: "teal", hint: "Pay to start production. A proof comes before anything prints." },
  paid: { label: "Paid", chip: "green", hint: "Payment received — being sent to the print desk." },
  submitted: { label: "At the printer", chip: "green", hint: "Submitted to the print provider; proof on its way." },
  in_production: { label: "Printing", chip: "green", hint: "Proof approved, on press." },
  shipped: { label: "Shipped", chip: "green", hint: "On the way." },
  delivered: { label: "Delivered", chip: "", hint: "" },
  cancelled: { label: "Cancelled", chip: "", hint: "" },
  refunded: { label: "Refunded", chip: "", hint: "" },
};
export const cents = (c: number | null | undefined) => (c == null ? "—" : "$" + (c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
